import { Box, ButtonBase, styled } from '@mui/material';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { DynamicIcon, ShortcutText, Tooltip } from 'src/components';
import { Cue } from 'src/components/Cue';
import { useScrollEdges } from 'src/components/Navbar/DesktopDrawer/use-scroll-edges';
import { appShortcuts, getNavigationTabShortcut } from 'src/constants/appShortcuts';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { useNotificationCount } from 'src/graphql/entities/notification/hooks/use-notification-count';
import { useRegisterShortcuts } from 'src/utils/hooks';
import {
  getVisibleNavGroupTabShortcuts,
  type NavGroupTabId,
  type NavigationPane,
} from './nav-group-tab-shortcuts';
import { getMovedTabSx } from './SidekickPane/sidekick-transition.styles';
import { useSidekickTransition } from './SidekickPane/use-sidekick-transition';

export type { NavigationPane } from './nav-group-tab-shortcuts';

const EDGE_FADE = '1.25rem';

const TAB_MOTION_MS = 180;

// the tab that just became active grows a label and shifts sideways, so anchoring
// the cue before the motion settles leaves its arrow off the button's centre
const MOVED_CUE_DELAY_MS = TAB_MOTION_MS + 40;

const getEdgeMask = (atStart: boolean, atEnd: boolean): string | undefined => {
  if (atStart && atEnd) {
    return `linear-gradient(to right, transparent, #000 ${EDGE_FADE}, #000 calc(100% - ${EDGE_FADE}), transparent)`;
  }
  if (atEnd) return `linear-gradient(to right, #000 calc(100% - ${EDGE_FADE}), transparent)`;
  if (atStart) return `linear-gradient(to left, #000 calc(100% - ${EDGE_FADE}), transparent)`;
  return undefined;
};

const TabsRow = styled(Box, { name: 'NavGroupTabsRow' })(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  // at the narrowest nav width the buttons no longer fit
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  // height matches the grid FILTER toolbar so the two rows line up
  height: '3.225rem',
  boxSizing: 'border-box',
  padding: '0 0.75rem',
  flexShrink: 0,
}));

// collapses once the row overflows, so Settings sits with the rest
const TabSpacer = styled(Box, { name: 'NavGroupTabSpacer' })({ flex: 1, minWidth: 0 });

const TabButton = styled(ButtonBase, {
  name: 'NavGroupTab',
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => {
  const activeBackground = theme.palette.custom.OV.sys.surfaceContainer;

  return {
    position: 'relative',
    minWidth: '2.125rem',
    height: '1.875rem',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // no gap: icon-to-label spacing lives on the label so it collapses with it
    padding: active ? '0 0.75rem' : '0 0.375rem',
    border: 0,
    borderRadius: '0.5rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: active
      ? theme.palette.custom.OV.sys.onBackground
      : theme.palette.custom.OV.sys.onSurfaceVariant,
    backgroundColor: active ? activeBackground : 'transparent',
    boxShadow: active
      ? `inset 0 0 0 1px ${theme.palette.custom.OV.sys.outlineVariant}`
      : 'none',
    transition: `background-color .12s, color .12s, padding ${TAB_MOTION_MS}ms ease`,

    '&:hover': {
      backgroundColor: active
        ? activeBackground
        : theme.palette.custom.OV.sys.surfaceContainerHighest,
      color: theme.palette.custom.OV.sys.onBackground,
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  };
});

// stays mounted: unmounting drops the text width in one frame while the padding
// keeps easing, which reads as a jump mid-animation
const TabLabel = styled('span', {
  name: 'NavGroupTabLabel',
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'grid',
  // 0fr->1fr resolves to the exact text width; a max-width cap lands early
  gridTemplateColumns: active ? '1fr' : '0fr',
  marginLeft: active ? '0.375rem' : 0,
  opacity: active ? 1 : 0,
  transition: `grid-template-columns ${TAB_MOTION_MS}ms ease, margin-left ${TAB_MOTION_MS}ms ease, opacity .12s ease`,

  '& > span': { overflow: 'hidden', minWidth: 0 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
}));

// anchors the badge to the icon, so expanding the tab with a label can't drag the counter away
const TabIconSlot = styled('span', { name: 'NavGroupTabIconSlot' })({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const TabBadge = styled('span', { name: 'NavGroupTabBadge' })(({ theme }) => ({
  position: 'absolute',
  top: '-5px',
  right: '-6px',
  display: 'grid',
  placeItems: 'center',
  minWidth: '0.875rem',
  height: '0.875rem',
  borderRadius: '999px',
  backgroundColor: theme.palette.custom.OV.sys.error,
  boxShadow: `0 0 0 2px ${theme.palette.custom.OV.sys.surfaceContainerHigh}`,
  fontSize: '0.5625rem',
  fontWeight: 700,
  lineHeight: 1,
  color: theme.palette.custom.OV.sys.surfaceContainerLowest,
  padding: '0 0.1875rem',
  pointerEvents: 'none',
  fontVariantNumeric: 'tabular-nums',
}));

const TAB_ICON_SIZE = 18;

const MOVED_CUE_TIMEOUT_MS = 7000;

type NavGroupTabDefinition = {
  id: NavGroupTabId;
  label: string;
  show: boolean;
  onClick: () => void;
};

export interface NavGroupTabsViewProps {
  activeTab: NavigationPane;
  inboxBadgeCount?: number;
  showInboxTab?: boolean;
  showSearchTab?: boolean;
  showSidekickTab?: boolean;
  showMovedCue?: boolean;
  showSettingsTab?: boolean;
  onHomeClick: () => void;
  onInboxClick: () => void;
  onSearchClick: () => void;
  onSidekickClick: () => void;
  onSettingsClick: () => void;
  tabShortcuts?: Partial<Record<NavGroupTabId, string>>;
}

const NavTabTooltip = ({
  label,
  shortcut,
  children,
}: {
  label: string;
  shortcut?: string;
  children: ReactElement;
}) => (
  <Tooltip
    title={shortcut ? <ShortcutText label={label} shortcut={shortcut} size="small" /> : label}
  >
    {children}
  </Tooltip>
);

export const NavGroupTabsView = ({
  activeTab,
  inboxBadgeCount,
  showInboxTab = true,
  showSearchTab = true,
  showSidekickTab = true,
  showMovedCue = false,
  showSettingsTab = true,
  onHomeClick,
  onInboxClick,
  onSearchClick,
  onSidekickClick,
  onSettingsClick,
  tabShortcuts,
}: NavGroupTabsViewProps) => {
  const {
    ref: rowRef,
    edges,
    syncEdges,
  } = useScrollEdges<HTMLDivElement>({ axis: 'horizontal' });
  const [sidekickTabEl, setSidekickTabEl] = useState<HTMLElement | null>(null);
  const [isCueAnchored, setIsCueAnchored] = useState(false);

  useEffect(() => {
    if (!showMovedCue) {
      setIsCueAnchored(false);
      return;
    }
    const settled = setTimeout(() => setIsCueAnchored(true), MOVED_CUE_DELAY_MS);
    return () => clearTimeout(settled);
  }, [showMovedCue]);

  // the active tab grows a label, which can push it out of view
  useEffect(() => {
    const revealActiveTab = () => {
      rowRef.current
        ?.querySelector(`[data-testid="nav-group-tab-${activeTab}"]`)
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      syncEdges();
    };

    revealActiveTab();
    // the tab's final width is only measurable once the label motion has settled
    const settled = setTimeout(revealActiveTab, TAB_MOTION_MS);
    return () => clearTimeout(settled);
  }, [activeTab, syncEdges, rowRef]);

  const mask = getEdgeMask(edges.start, edges.end);

  return (
    <>
      <TabsRow
        ref={rowRef}
        onScroll={syncEdges}
        data-testid="nav-group-tabs"
        role="tablist"
        sx={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
      >
        <NavTabTooltip label="Home" shortcut={tabShortcuts?.home}>
          <TabButton
            disableRipple
            active={activeTab === 'home'}
            onClick={onHomeClick}
            data-testid="nav-group-tab-home"
          >
            <DynamicIcon name="home" library="lucide" size={TAB_ICON_SIZE} />
            <TabLabel active={activeTab === 'home'} data-expanded={activeTab === 'home'}>
              <span>Home</span>
            </TabLabel>
          </TabButton>
        </NavTabTooltip>
        {showInboxTab ? (
          <NavTabTooltip label="Inbox" shortcut={tabShortcuts?.inbox}>
            <TabButton
              disableRipple
              active={activeTab === 'inbox'}
              onClick={onInboxClick}
              data-testid="nav-group-tab-inbox"
            >
              <TabIconSlot>
                <DynamicIcon name="inbox" library="lucide" size={TAB_ICON_SIZE} />
                {inboxBadgeCount ? <TabBadge>{inboxBadgeCount}</TabBadge> : null}
              </TabIconSlot>
              <TabLabel active={activeTab === 'inbox'} data-expanded={activeTab === 'inbox'}>
                <span>Inbox</span>
              </TabLabel>
            </TabButton>
          </NavTabTooltip>
        ) : null}
        {showSearchTab ? (
          <NavTabTooltip
            label="Search"
            shortcut={tabShortcuts?.search ?? appShortcuts.openSearch.command}
          >
            <TabButton
              disableRipple
              active={activeTab === 'search'}
              onClick={onSearchClick}
              data-testid="nav-group-tab-search"
            >
              <DynamicIcon name="search" library="lucide" size={TAB_ICON_SIZE} />
              <TabLabel active={activeTab === 'search'} data-expanded={activeTab === 'search'}>
                <span>Search</span>
              </TabLabel>
            </TabButton>
          </NavTabTooltip>
        ) : null}
        {showSidekickTab ? (
          <NavTabTooltip label="Sidekick" shortcut={tabShortcuts?.sidekick}>
            <TabButton
              disableRipple
              active={activeTab === 'sidekick'}
              onClick={onSidekickClick}
              data-testid="nav-group-tab-sidekick"
              sx={showMovedCue ? getMovedTabSx : undefined}
              ref={setSidekickTabEl}
            >
              <DynamicIcon name="sparkles" library="lucide" size={TAB_ICON_SIZE} />
              <TabLabel
                active={activeTab === 'sidekick'}
                data-expanded={activeTab === 'sidekick'}
              >
                <span>Sidekick</span>
              </TabLabel>
            </TabButton>
          </NavTabTooltip>
        ) : null}
        <TabSpacer />
        {showSettingsTab ? (
          <NavTabTooltip label="Settings" shortcut={tabShortcuts?.settings}>
            <TabButton
              disableRipple
              active={activeTab === 'settings'}
              onClick={onSettingsClick}
              data-testid="nav-group-tab-settings"
            >
              <DynamicIcon name="settings" library="lucide" size={TAB_ICON_SIZE} />
              <TabLabel
                active={activeTab === 'settings'}
                data-expanded={activeTab === 'settings'}
              >
                <span>Settings</span>
              </TabLabel>
            </TabButton>
          </NavTabTooltip>
        ) : null}
      </TabsRow>
      <Cue
        open={Boolean(showMovedCue && isCueAnchored && sidekickTabEl)}
        anchorEl={sidekickTabEl}
        data-testid="sidekick-moved-cue"
      >
        Sidekick moved up here
      </Cue>
    </>
  );
};

interface NavGroupTabsProps {
  activePane: NavigationPane;
  onPaneChange: (pane: NavigationPane) => void;
  hasSettingsItems: boolean;
}

export const NavGroupTabs = ({
  activePane,
  onPaneChange,
  hasSettingsItems,
}: NavGroupTabsProps) => {
  const { unreadCount } = useNotificationCount();
  const { checkValidity, loading: rulesLoading } = useGroupRules();
  const { hasTransitioned, isMovedCueVisible, dismissMovedCue } = useSidekickTransition();

  const showInboxTab = !rulesLoading ? checkValidity([UserGroupRules.AccessToInbox]) : false;
  const showSearchTab = !rulesLoading
    ? checkValidity([UserGroupRules.AccessToViewConversations])
    : false;
  // Sidekick always carries Help, so no rule gates the tab; before the move the
  // bottom bar owns the entry point
  const showSidekickTab = hasTransitioned;
  const showSettingsTab = hasSettingsItems;

  const onHomeClick = useCallback(() => onPaneChange('home'), [onPaneChange]);
  const onInboxClick = useCallback(() => onPaneChange('inbox'), [onPaneChange]);
  const onSearchClick = useCallback(() => onPaneChange('search'), [onPaneChange]);
  const onSidekickClick = useCallback(() => {
    dismissMovedCue();
    onPaneChange('sidekick');
  }, [dismissMovedCue, onPaneChange]);

  // wall-clock deadline with no event to hang it off, so a timer is the mechanism
  useEffect(() => {
    if (!isMovedCueVisible) {
      return undefined;
    }
    const timer = setTimeout(dismissMovedCue, MOVED_CUE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isMovedCueVisible, dismissMovedCue]);

  const onSettingsClick = useCallback(() => onPaneChange('settings'), [onPaneChange]);

  const visibleTabs = useMemo<NavGroupTabDefinition[]>(
    () => [
      { id: 'home', label: 'Home', show: true, onClick: onHomeClick },
      { id: 'inbox', label: 'Inbox', show: showInboxTab, onClick: onInboxClick },
      { id: 'search', label: 'Search', show: showSearchTab, onClick: onSearchClick },
      { id: 'sidekick', label: 'Sidekick', show: showSidekickTab, onClick: onSidekickClick },
      { id: 'settings', label: 'Settings', show: showSettingsTab, onClick: onSettingsClick },
    ],
    [
      showInboxTab,
      showSearchTab,
      showSidekickTab,
      showSettingsTab,
      onHomeClick,
      onInboxClick,
      onSearchClick,
      onSidekickClick,
      onSettingsClick,
    ],
  );

  const tabShortcuts = useMemo(
    () => getVisibleNavGroupTabShortcuts(visibleTabs),
    [visibleTabs],
  );

  useRegisterShortcuts(
    useMemo(
      () =>
        visibleTabs
          .filter((tab) => tab.show)
          .map((tab, index) => ({
            ...getNavigationTabShortcut(index + 1, tab.label),
            callback: () => {
              tab.onClick();
              return false;
            },
          })),
      [visibleTabs],
    ),
  );

  return (
    <NavGroupTabsView
      activeTab={activePane}
      inboxBadgeCount={unreadCount}
      showInboxTab={showInboxTab}
      showSearchTab={showSearchTab}
      showSidekickTab={showSidekickTab}
      showMovedCue={isMovedCueVisible}
      showSettingsTab={showSettingsTab}
      tabShortcuts={tabShortcuts}
      onHomeClick={onHomeClick}
      onInboxClick={onInboxClick}
      onSearchClick={onSearchClick}
      onSidekickClick={onSidekickClick}
      onSettingsClick={onSettingsClick}
    />
  );
};
